import { ChangeEvent, useCallback, useEffect, useState } from "react";
import {
  getNextPriceBookCode,
  getPriceBookDetails,
} from "../../../services";
import {
  PriceBookWithCounts,
  PricebookValidity,
} from "../../../types/price-book.type";
import { featureAccess } from "../../../permissions/access";
import { todayInBangkok } from "../../../utils/date";
import { useUserRole } from "../../../utils/hooks/useUserRole";
import { COMPACT_QUERY } from "../../../utils/hooks/useCompact";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import Drawer from "@mui/material/Drawer";
import FormControlLabel from "@mui/material/FormControlLabel";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import Typography from "@mui/material/Typography";
import DeletePriceBookButton from "./drawer/DeletePriceBookButton";
import GeneralInfoFields from "./drawer/GeneralInfoFields";
import ValidityFields from "./drawer/ValidityFields";
import { savePriceBook } from "./drawer/savePriceBook";
import { ValidityForm, validateValidity } from "./drawer/validityForm";
import {
  DUPLICATE_START_NOTICE,
  HEAD_OFFICE_START_NOTICE,
  INITIAL_FORM,
  INITIAL_VALIDITY,
  LOAD_DETAILS_FAILED,
  NEXT_CODE_FAILED,
  TITLE_BY_MODE,
  toPriceBookForm,
} from "./drawer/formState";
import { isValidDiscountPercent } from "./Details/lineEdits";
import { DRAWER_STATUS_LABEL, toPriceBookType } from "./constants";
import {
  PriceBookDrawerMode,
  PriceBookFormErrors,
  PriceBookFormState,
} from "./types";

const compact = "@media " + COMPACT_QUERY;

const DRAWER_WIDTH = 560;

const NAME_REQUIRED = "กรุณากรอกชื่อสมุดราคา";
const DISCOUNT_INVALID = "กรอกตัวเลข 0–100 ทศนิยมไม่เกิน 2 ตำแหน่ง";

const START_NOTICE_BY_MODE: Record<PriceBookDrawerMode, string> = {
  create: HEAD_OFFICE_START_NOTICE,
  duplicate: DUPLICATE_START_NOTICE,
  edit: "",
};

/** The next free code, or "" when the API answered the create form without one. */
const fetchNextPriceBookCode = async (): Promise<string> => {
  const {
    data: { data },
  } = await getNextPriceBookCode();
  return data?.code ?? "";
};

interface PriceBookDrawerProps {
  open: boolean;
  mode: PriceBookDrawerMode;
  /** The source pricebook for edit and duplicate; empty when creating. */
  priceBookUuid: string;
  /**
   * Whether the book's days of validity are offered. The API refuses them on a draft and on the
   * books the system settings name; a new book and a copy always take them.
   */
  canSetValidity: boolean;
  onClose: () => void;
  /** Called with the pricebook the save wrote — for a duplicate, the new copy. */
  onSaved: (uuid: string) => void;
  /** The pricebook this drawer edited no longer exists, so its screen must move on. */
  onDeleted: () => void;
}

/**
 * The pricebook header: create a book (it opens in 00-HO with every product at its
 * ราคาหน้าซอง less the book %), edit its name / type / status, or copy it as a new book.
 */
export default function PriceBookDrawer({
  open,
  mode,
  priceBookUuid,
  canSetValidity,
  onClose,
  onSaved,
  onDeleted,
}: PriceBookDrawerProps) {
  const role = useUserRole();
  const canEditCatalogue = featureAccess.productWrite(role);

  const [form, setForm] = useState<PriceBookFormState>({ ...INITIAL_FORM });
  const [errors, setErrors] = useState<PriceBookFormErrors>({});
  const [alertMessage, setAlertMessage] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  // An edit or a copy sends values read from the record, so nothing may be sent before they
  // arrived; a create waits for its code.
  const [detailsLoaded, setDetailsLoaded] = useState<boolean>(false);
  const [loadFailed, setLoadFailed] = useState<boolean>(false);
  // The record as loaded: its counts and status drive the delete button.
  const [storedPriceBook, setStoredPriceBook] =
    useState<PriceBookWithCounts | null>(null);

  const isDuplicate = mode === "duplicate";
  const canDelete = mode === "edit" && canEditCatalogue;
  // An edit compares the validity with the stored one; a new book or a copy has none yet.
  const storedValidity: PricebookValidity | null =
    mode === "edit" ? storedPriceBook : null;

  const resetState = useCallback(() => {
    setForm({ ...INITIAL_FORM });
    setErrors({});
    setAlertMessage("");
    setStoredPriceBook(null);
    setDetailsLoaded(false);
    setLoadFailed(false);
  }, []);

  // The code cannot be typed, so an empty one keeps the save shut rather than create a
  // pricebook without one.
  const applyNextCode = useCallback((nextCode: string) => {
    setForm((prev) => ({ ...prev, code: nextCode }));
    setDetailsLoaded(Boolean(nextCode));
    setLoadFailed(!nextCode);
  }, []);

  useEffect(() => {
    if (!open) return;
    resetState();
    const isCreate = mode === "create";
    if (!isCreate && !priceBookUuid) return;

    // Closing the drawer, or reopening it on another pricebook, makes an in-flight load stale.
    let ignore = false;

    const loadNextCode = async () => {
      try {
        const nextCode = await fetchNextPriceBookCode();
        if (!ignore) applyNextCode(nextCode);
      } catch {
        // Reported globally by the axios interceptor; the drawer keeps the save shut.
        if (!ignore) applyNextCode("");
      }
    };

    const loadPriceBook = async () => {
      try {
        const {
          data: { data },
        } = await getPriceBookDetails(priceBookUuid);
        if (ignore) return;
        const loadedForm = toPriceBookForm(data);
        // A copy is issued a fresh code on save, so the source's is not shown for it, and it
        // never inherits the source's validity.
        setForm(
          isDuplicate ? { ...loadedForm, code: "", ...INITIAL_VALIDITY } : loadedForm,
        );
        setStoredPriceBook(data);
        setDetailsLoaded(true);
      } catch {
        // Reported globally by the axios interceptor; the drawer keeps the save shut.
        if (!ignore) setLoadFailed(true);
      }
    };

    const load = async () => {
      setLoading(true);
      await (isCreate ? loadNextCode() : loadPriceBook());
      if (!ignore) setLoading(false);
    };
    load();

    return () => {
      ignore = true;
    };
  }, [open, mode, isDuplicate, priceBookUuid, resetState, applyNextCode]);

  // The prefilled code is not reserved: another user can create under it first, and the
  // retry then needs the next free one already in the form.
  const refillNextCode = async (): Promise<boolean> => {
    try {
      const nextCode = await fetchNextPriceBookCode();
      applyNextCode(nextCode);
      return Boolean(nextCode);
    } catch {
      // Reported globally by the axios interceptor; without a code nothing can be saved.
      applyNextCode("");
      return false;
    }
  };

  const handleChangeName = (event: ChangeEvent<HTMLInputElement>) => {
    setForm((prev) => ({ ...prev, name: event.target.value }));
    setErrors((prev) => ({ ...prev, name: "" }));
  };

  const handleChangeType = (event: ChangeEvent<HTMLInputElement>) => {
    const type = toPriceBookType(event.target.value);
    // The select offers exactly the two known types; any other value is not one the form
    // can hold, so the current type stands.
    if (!type) return;
    setForm((prev) => ({ ...prev, type }));
  };

  const handleChangeDiscountPercent = (discountPercent: string) => {
    setForm((prev) => ({ ...prev, discountPercent }));
    setErrors((prev) => ({ ...prev, discountPercent: "" }));
  };

  const handleToggleStatus = (event: ChangeEvent<HTMLInputElement>) => {
    const status = event.target.checked ? "enable" : "disable";
    setForm((prev) => ({ ...prev, status }));
  };

  const handleChangeValidity = (changes: Partial<ValidityForm>) => {
    setForm((prev) => ({ ...prev, ...changes }));
    setErrors((prev) => ({ ...prev, effectiveFrom: "", effectiveTo: "" }));
  };

  const validate = (): boolean => {
    const nextErrors: PriceBookFormErrors = canSetValidity
      ? validateValidity(form, todayInBangkok(), storedValidity)
      : {};
    if (!form.name.trim()) nextErrors.name = NAME_REQUIRED;
    if (mode !== "edit" && !isValidDiscountPercent(form.discountPercent)) {
      nextErrors.discountPercent = DISCOUNT_INVALID;
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleClose = () => {
    if (saving) return;
    onClose();
  };

  const handleSave = async () => {
    if (!detailsLoaded || !validate()) return;
    setAlertMessage("");
    setSaving(true);
    try {
      const outcome = await savePriceBook({
        mode,
        sourceUuid: priceBookUuid,
        form,
        storedValidity,
      });
      if (outcome.codeTaken) {
        // A refill that failed already keeps the save shut and says why.
        const refilled = await refillNextCode();
        if (refilled) setAlertMessage(outcome.errorMessage);
        return;
      }
      if (outcome.errorMessage) {
        setAlertMessage(outcome.errorMessage);
        return;
      }
      onSaved(outcome.uuid);
      onClose();
    } finally {
      setSaving(false);
    }
  };

  const handleDeleted = () => {
    onDeleted();
    onClose();
  };

  // The form still holds the status it loaded with, so it closes rather than let a later
  // save switch the pricebook back on.
  const handleDeactivated = () => {
    onSaved(priceBookUuid);
    onClose();
  };

  const isBusy = loading || saving;
  const loadFailedMessage =
    mode === "create" ? NEXT_CODE_FAILED : LOAD_DETAILS_FAILED;
  const startNotice = START_NOTICE_BY_MODE[mode];
  const statusLabel = DRAWER_STATUS_LABEL[form.status];
  // Without a delete action the footer has one group left, which belongs on the right.
  const footerAlignment = canDelete ? "space-between" : "flex-end";

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      slotProps={{
        paper: {
          sx: {
            width: { xs: "100%", md: DRAWER_WIDTH },
            maxWidth: "100%",
            p: 0,
          },
        },
      }}
    >
      <Stack sx={{ height: "100%" }}>
        <Box sx={{ p: 3, pb: 0, [compact]: { p: 2, pb: 0 } }}>
          <Typography variant="body1" fontWeight={600} gutterBottom>
            {TITLE_BY_MODE[mode]}
          </Typography>
          <Divider />
        </Box>

        <Box sx={{ p: 3, [compact]: { p: 2 }, overflowY: "auto", flex: 1 }}>
          {loading ? (
            <Stack alignItems="center" sx={{ py: 8 }}>
              <CircularProgress />
            </Stack>
          ) : (
            <>
              {loadFailed && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {loadFailedMessage}
                </Alert>
              )}
              {alertMessage && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  {alertMessage}
                </Alert>
              )}
              {startNotice && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  {startNotice}
                </Alert>
              )}

              <FormControlLabel
                sx={{ mb: 2, [compact]: { mb: 1.25 } }}
                control={
                  <Switch
                    checked={form.status === "enable"}
                    onChange={handleToggleStatus}
                    disabled={isBusy}
                  />
                }
                label={statusLabel}
              />

              <GeneralInfoFields
                mode={mode}
                form={form}
                errors={errors}
                disabled={isBusy}
                onChangeName={handleChangeName}
                onChangeType={handleChangeType}
                onChangeDiscountPercent={handleChangeDiscountPercent}
              />

              {canSetValidity && (
                <ValidityFields
                  form={form}
                  errors={errors}
                  disabled={isBusy}
                  storedValidity={storedValidity}
                  onChange={handleChangeValidity}
                />
              )}
            </>
          )}
        </Box>

        <Stack
          direction="row"
          alignItems="center"
          justifyContent={footerAlignment}
          gap={2}
          sx={{
            p: 3,
            pt: 2,
            [compact]: { p: 2, pt: 1.5 },
            borderTop: (theme) => `1px solid ${theme.palette.draft.main}`,
            bgcolor: (theme) => theme.palette.background.paper,
          }}
        >
          {canDelete && (
            <DeletePriceBookButton
              priceBook={storedPriceBook}
              disabled={isBusy}
              onDeleted={handleDeleted}
              onDeactivated={handleDeactivated}
              onRefused={setAlertMessage}
            />
          )}
          <Stack direction="row" gap={2}>
            <Button disabled={saving} onClick={handleClose}>
              ยกเลิก
            </Button>
            <Button
              loading={saving}
              disabled={!detailsLoaded}
              variant="contained"
              onClick={handleSave}
            >
              บันทึก
            </Button>
          </Stack>
        </Stack>
      </Stack>
    </Drawer>
  );
}
